import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, chakra } from '@chakra-ui/react';
import {
  Heading,
  HStack,
  Pressable,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { ColorAudit } from './ColorAudit';
import { TypographyAudit } from './TypographyAudit';
import { IconAudit } from './IconAudit';
import { ComponentAudit } from './ComponentAudit';
import { MoleculeAudit } from './MoleculeAudit';
import { PrimitiveAudit } from './PrimitiveAudit';
import { FeatureAudit } from './FeatureAudit';
import { Archive } from './Archive';
import { PackageAudit } from './PackageAudit';
import { ChangeLog } from './ChangeLog';

type View = 'prototype' | 'archive' | 'repo' | 'changelog';

/**
 * Height of the sticky nav plus a little breathing room. A section counts as
 * "in view" once its heading has passed under the nav — matching where the eye
 * actually is, rather than where the raw scroll offset is.
 */
const NAV_OFFSET = 84;

const SECTIONS = [
  { id: 'colors', label: 'Colors' },
  { id: 'typography', label: 'Typography' },
  { id: 'icons', label: 'Icons' },
  { id: 'atoms', label: 'Atoms' },
  { id: 'molecules', label: 'Molecules' },
  { id: 'primitives', label: 'Primitives' },
  { id: 'patterns', label: 'Patterns' },
];

const SECTION_IDS = SECTIONS.map(({ id }) => id);

/**
 * Which section the reader is currently in.
 *
 * A scroll listener rather than an IntersectionObserver: these sections vary
 * enormously in height — Colors is a few rows, Atoms is most of a screenful of
 * cards — so "which one is intersecting" is ambiguous for long stretches, while
 * "which heading did I last scroll past" is not.
 *
 * The page scrolls an inner container, not the window, so both the listener and
 * the measurements are relative to that element.
 */
const useActiveSection = (
  scrollerRef: React.RefObject<HTMLDivElement | null>,
  enabled: boolean
) => {
  const [activeId, setActiveId] = useState(SECTION_IDS[0]);

  const measure = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const line = scroller.getBoundingClientRect().top + NAV_OFFSET;
    let current = SECTION_IDS[0];
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el && el.getBoundingClientRect().top <= line) current = id;
    });

    // At the very bottom the last section is what you are reading, even though
    // its heading is far above the line — without this the final entry can
    // never become active if it is shorter than the viewport.
    const atBottom =
      scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2;
    if (atBottom) current = SECTION_IDS[SECTION_IDS.length - 1];

    setActiveId((prev) => (prev === current ? prev : current));
  }, [scrollerRef]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!enabled || !scroller) return;

    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };

    measure();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [enabled, measure, scrollerRef]);

  return activeId;
};

const TabButton = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  // The selected tab now separates from the rest on four axes at once — fill,
  // a seafoam ring, weight, and elevation — rather than fill alone. These tabs
  // switch the whole page, so "which one am I on" has to be answerable at a
  // glance from anywhere on it, and the unselected tabs are muted to widen the
  // gap rather than relying on the active one shouting.
  <Pressable onClick={onClick} w="auto">
    <Box
      px={4}
      py="6px"
      rounded="full"
      transition="background-color 120ms ease, border-color 120ms ease"
      bg={active ? 'evergreen' : 'transparent'}
      borderWidth="1px"
      borderColor={active ? 'evergreen' : 'cloud'}
      boxShadow={active ? '0 0 0 3px rgba(45, 102, 112, 0.18)' : 'none'}
      _hover={active ? undefined : { borderColor: 'bluegrey' }}
    >
      <Text
        my={0}
        fontSize="sm"
        fontWeight={active ? 700 : 500}
        color={active ? 'white' : 'muted.400'}
      >
        {children}
      </Text>
    </Box>
  </Pressable>
);

const InPageNav = ({ activeId }: { activeId: string }) => (
  <HStack
    gap={4}
    wrap="wrap"
    position="sticky"
    top={0}
    zIndex={1}
    bg="canvas"
    borderBottomWidth="1px"
    borderColor="cloud"
    pt={3}
  >
    {SECTIONS.map(({ id, label }) => {
      const active = id === activeId;
      return (
        <chakra.a
          key={id}
          href={`#${id}`}
          pb={2}
          fontSize="sm"
          // Two signals rather than one: weight carries at a glance, the rule
          // says precisely which item. Colour alone would not be enough.
          fontWeight={active ? 700 : 500}
          color={active ? 'seafoam' : 'bluegrey'}
          borderBottomWidth="2px"
          borderColor={active ? 'seafoam' : 'transparent'}
          textDecoration="none"
          transition="color 120ms ease, border-color 120ms ease"
          _hover={{ color: 'seafoam' }}
        >
          {label}
        </chakra.a>
      );
    })}
  </HStack>
);

/**
 * Living design-system audit. The Prototype tab is rendered from the live
 * theme config + component library and the patterns audited out of the real
 * homepage / patient-profile / labs screens. The Archive tab collects
 * legacy + deprecated patterns so it's clear what Eng needs to clean up.
 */
export const DesignSystemPage = () => {
  const [view, setView] = useState<View>('prototype');
  const scrollerRef = useRef<HTMLDivElement>(null);
  const activeId = useActiveSection(scrollerRef, view === 'prototype');

  return (
    <Box
      ref={scrollerRef}
      h="100%"
      w="100%"
      overflowY="auto"
      bg="canvas"
      px={8}
      pb={16}
    >
      <VStack gap={6} align="stretch" maxW="1100px" mx="auto">
        <VStack gap={1} align="stretch" pt={8}>
          <Heading size="xl">Design system audit</Heading>
          <Text maxW="760px">
            The Prototype tab is rendered from the live theme config and
            component library — not hand-copied — then audited against the real
            homepage, patient profile, and labs screens. The Repo tab shows
            everything @ayble-health/dashboard-components exports, so you can
            compare what is implemented upstream against what this app uses.
          </Text>
        </VStack>

        <HStack gap={2} align="center">
          <TabButton
            active={view === 'prototype'}
            onClick={() => setView('prototype')}
          >
            Prototype
          </TabButton>
          <TabButton
            active={view === 'changelog'}
            onClick={() => setView('changelog')}
          >
            What&apos;s Changed
          </TabButton>
          <TabButton active={view === 'repo'} onClick={() => setView('repo')}>
            Repo
          </TabButton>
          <TabButton
            active={view === 'archive'}
            onClick={() => setView('archive')}
          >
            Archive
          </TabButton>
        </HStack>

        {view === 'prototype' && (
          <>
            <InPageNav activeId={activeId} />
            <ColorAudit />
            <TypographyAudit />
            <IconAudit />
            <ComponentAudit />
            <MoleculeAudit />
            <PrimitiveAudit />
            <FeatureAudit />
          </>
        )}
        {view === 'changelog' && <ChangeLog />}
        {view === 'repo' && <PackageAudit />}
        {view === 'archive' && <Archive />}
      </VStack>
    </Box>
  );
};
